import S3 from 'aws-sdk/clients/s3';
import { fetchAuthSession } from 'aws-amplify/auth';
import { gql } from '@apollo/client';
import config from 'aws-exports';

import { apolloClient } from 'core/apollo-client';

// GraphQL queries using existing operations
const GET_OFFER_DOCUMENTS = gql`
  query GetOfferDocuments($webUserId: ID!) {
    getDocs(webUserId: $webUserId) {
      internal {
        path
        lastModified
      }
    }
  }
`;

const GET_UPLOAD_URL = gql`
  query GetUploadUrl($webUserId: ID!, $fileName: String!, $mimeType: String!) {
    getUploadUrl(webUserId: $webUserId, fileName: $fileName, mimeType: $mimeType) {
      url
    }
  }
`;

/**
 * Get upload URL and upload file to S3
 */
export const uploadOfferDocument = async (webUserId, file) => {
  try {
    // Step 1: Get upload URL
    const { data: uploadUrlData, error } = await apolloClient.query({
      query: GET_UPLOAD_URL,
      variables: {
        webUserId,
        fileName: file.name,
        mimeType: file.type
      }
    });

    const { url: uploadUrl } = uploadUrlData.getUploadUrl;

    // Step 2: Upload file to S3
    await uploadFileToS3(uploadUrl, file);

    // Step 3: Return document info (following existing pattern)
    // The document will appear in subsequent getDocs calls
    return {
      id: `offer/${webUserId}/${file.name}`,
      fileName: file.name,
      uploadedAt: new Date().toISOString()
    };
  } catch (error) {
    console.error('Error uploading offer document:', error);
    throw new Error('Failed to upload document');
  }
};

/**
 * Upload file to S3 using presigned URL
 */
const uploadFileToS3 = (uploadUrl, file) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status: ${xhr.status}`));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed'));
    });

    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', file.type);
    xhr.send(file);
  });
};

/**
 * Fetch all documents for a webUserId
 */
export const fetchOfferDocuments = async (webUserId) => {
  try {
    const { data } = await apolloClient.query({
      query: GET_OFFER_DOCUMENTS,
      variables: { webUserId },
      fetchPolicy: 'no-cache' // Always fetch fresh data
    });

    const documents = data.getDocs?.internal || [];

    // Transform the documents to match expected format
    return documents.map((doc) => {
      const fileName = doc.path.split('/').pop(); // Extract filename from path
      return {
        id: doc.path,
        fileName,
        uploadedAt: doc.lastModified,
        path: doc.path
      };
    });
  } catch (error) {
    console.error('Error fetching offer documents:', error);
    throw new Error('Failed to fetch documents');
  }
};

/**
 * Download a document
 */
export const downloadOfferDocument = async (path, secondsBeforeExpire = 86400) => {
  try {
    const { credentials } = await fetchAuthSession();
    const s3Client = new S3({
      region: config.Storage.region,
      credentials: {
        accessKeyId: credentials.accessKeyId,
        secretAccessKey: credentials.secretAccessKey,
        sessionToken: credentials.sessionToken
      }
    });
    const url = s3Client.getSignedUrl('getObject', {
      Key: path,
      Bucket: config.Storage.bucket,
      Expires: secondsBeforeExpire
    });
    window.open(url, '_blank');
  } catch (error) {
    console.error('Error downloading document:', error);
  }
};
